import { useId, useState } from 'react';
import { useI18n } from '@platform/i18n';
import { Button } from '../components/button';
import { Checkbox } from '../components/checkbox';
import { Label } from '../components/label';
import { Skeleton } from '../components/misc';
import { toast } from '../components/sonner';
import { useAsync } from '../hooks/use-async';
import { useErrorText } from '../connected/errors';
import { XIcon } from 'lucide-react';
import type { TypedList } from '@platform/api-client';

export interface SharesAdapter {
  /** Replaces the groups the list is published to (the owner adds and removes; an administrator can only remove). */
  setShares(id: string, input: { expectedRevision: number; groups: string[] }, scope: { orgId?: 'all' | number; ownerId?: string }): Promise<TypedList>;
  /** Groups the signed-in user may publish to (owner view only; absent in the administrator view). */
  shareGroups?(): Promise<{ groups: { address: string; displayName: string }[] }>;
}

/**
 * Sharing tab of a password list: which groups may use the list's passwords for decryption. Values are never shown.
 * Owner view: a chooser over their own groups. Administrator view: the current shares with a revoke button.
 */
export function PasswordSharesPanel({ list, adapter, disabled, onSaved, onFailure }: {
  list: TypedList; adapter: SharesAdapter; disabled: boolean; onSaved: (list: TypedList) => void; onFailure: (message: string) => void;
}) {
  const { t } = useI18n();
  const errors = useErrorText();
  const idPrefix = useId();
  const current = (list.shares ?? []).map((share) => share.address);
  const [selected, setSelected] = useState<string[]>(current);
  const [busy, setBusy] = useState(false);
  const groups = useAsync(() => adapter.shareGroups?.() ?? Promise.resolve({ groups: [] }), list.id);
  const scope = { orgId: list.orgId, ownerId: list.ownerId };

  const save = async (next: string[], message: string) => {
    if (busy || disabled) return;
    setBusy(true);
    try {
      const saved = await adapter.setShares(list.id, { expectedRevision: list.revision, groups: next }, scope);
      setSelected((saved.shares ?? []).map((share) => share.address));
      onSaved(saved);
      toast.success(message);
    } catch (error) { onFailure(errors.describe(error)); }
    finally { setBusy(false); }
  };

  if (!adapter.shareGroups) {
    return <div className="space-y-2">
      <p className="text-sm text-muted-foreground">{t('typedLists.sharing.adminHint')}</p>
      {current.length === 0 ? <p className="text-sm text-muted-foreground">{t('typedLists.sharing.none')}</p> : <ul className="space-y-1" aria-label={t('typedLists.sharing.groups')}>
        {current.map((address) => <li key={address} className="flex items-center justify-between gap-2 rounded border p-2 text-sm">
          <span className="min-w-0 break-all">{address}</span>
          <Button variant="ghost" size="sm" disabled={busy || disabled} aria-label={t('typedLists.sharing.revoke', { name: address })} onClick={() => void save(current.filter((item) => item !== address), t('typedLists.sharing.revoked'))}><XIcon /></Button>
        </li>)}
      </ul>}
    </div>;
  }

  // A share whose group no longer lists the owner stays visible, so it can be revoked.
  const options = [...new Map([...current.map((address) => [address, address] as const), ...(groups.data?.groups ?? []).map((group) => [group.address, group.displayName] as const)]).entries()];
  const toggle = (address: string, on: boolean) => setSelected((now) => on ? [...new Set([...now, address])] : now.filter((item) => item !== address));
  const dirty = selected.length !== current.length || selected.some((address) => !current.includes(address));
  return <div className="space-y-2">
    <p className="text-sm text-muted-foreground">{t('typedLists.sharing.hint')}</p>
    {groups.error ? <p className="text-sm text-destructive" role="alert">{t('typedLists.sharing.loadError')}</p>
      : groups.loading && !groups.data ? <Skeleton className="h-16 w-full" />
        : options.length === 0 ? <p className="text-sm text-muted-foreground">{t('typedLists.sharing.noGroups')}</p>
          : <ul className="space-y-1" aria-label={t('typedLists.sharing.groups')}>{options.map(([address, label], index) => <li key={address} className="flex items-center gap-2 rounded border p-2 text-sm">
            <Checkbox id={`${idPrefix}-${index}`} checked={selected.includes(address)} disabled={busy || disabled} onCheckedChange={(on) => toggle(address, on === true)} />
            <Label htmlFor={`${idPrefix}-${index}`} className="min-w-0 break-all">{label === address ? address : `${label} (${address})`}</Label>
          </li>)}</ul>}
    <Button disabled={busy || disabled || !dirty} onClick={() => void save(selected, t('typedLists.sharing.saved'))}>{t('typedLists.sharing.save')}</Button>
  </div>;
}
